import React, { useState, useRef, useEffect, forwardRef } from 'react';
import RequesterForm from './PrepaidRequesterForm';
import {
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableRow,
   Paper,
   TextField,
   Button,
   Box,
   MenuItem,
   Select,
   FormHelperText,
 } from '@mui/material';
 import { initialFormData } from './PrepaidFormDependency/PrepaidModel';
 import FormAutoFiller from '../FormAutoFiller';
 import { 
   isBlankSimDataFilled, 
   isMsisdnDataFilled, 
   isStarterpackDataFilled,
   isSoftpinDataFilled,
   validateForm as validateFormData
 } from './PrepaidFormDependency/PrepaidValidation';
 import {
   handleInputChange as handleFormInputChange,
   clearFormData as clearFormState
 } from './PrepaidFormDependency/PrepaidFormDetailState';
 import PrepaidTestDataSection from './PrepaidFormDependency/PrepaidTestDataSection';
 import TotalQuantityDialog from '../TotalQuantityDialog';
 
 const PrepaidForm = forwardRef(({ onRef, existingWebSocket, wsConnectionId, initialData, onFormDataChange, requesterInitialData: requesterInitialDataProp }, ref) => {
   // State to store mappedRequesterData if a bedrockSRTicket is received
  const [formData, setFormData] = useState(() => {
     if (initialData) {

       return {
         ...initialFormData,
         ...initialData,
         general: {
           ...initialFormData.general,
           ...initialData.general,
           environment: (initialData.general && initialData.general.environment.toLowerCase())
         }
       };
     }
     return {
       ...initialFormData,
       general: {
         cr_project_name: initialFormData.general.cr_project_name || '',
         environment: initialFormData.general.environment || '',
         validity_period: initialFormData.general.validity_period || '',
       }
     };
   });

   useEffect(() => {
     if (onFormDataChange) {
       onFormDataChange(formData);
     }
   }, [formData, onFormDataChange]);

   const [showFullForm, setShowFullForm] = useState(true);
   const [showRequesterForm, setShowRequesterForm] = useState(false);
   const [showTotalQuantityDialog, setShowTotalQuantityDialog] = useState(false);
   const formRef = useRef(null);
   const [formFiller, setFormFiller] = useState(null);
   const [requesterInitialData, setRequesterInitialData] = useState(requesterInitialDataProp || null);
   const [requesterResetTrigger, setRequesterResetTrigger] = useState(null);
   const [errors, setErrors] = useState({
     general: {
       cr_project_name: '',
       validity_period: '',
       environment: ''
     }
   });
   const [userEmail, setUserEmail] = useState(null);
   const [formId] = useState(() => `Form_ID_${Math.random().toString(36).substr(2, 9)}`);
   
     useEffect(() => {
       console.log('PostpaidForm initialized with formId:', formId);
     }, [formId]);
 
   const handleSubmit = (updatedFormData) => {
     // No longer sending data to attachForm.py via getFormDetails
     // Form data will be sent later in the requester form with all data combined
     console.log('Prepaid form data prepared for submission:', updatedFormData);
   };

   const calculateTotalQuantity = (formData) => {
     let total = 0;
     const sections = ['Prepaid', 'Starter Pack', 'Softpin', 'Reload'];

     sections.forEach(section => {
       const sectionData = formData[section] || {};
       Object.values(sectionData).forEach(item => {
         if (item && typeof item === 'object' && item.quantity) {
           const quantity = String(item.quantity).trim();
           if (quantity !== '') {
             const num = Number(quantity);
             if (!Number.isNaN(num) && Number.isInteger(num) && num > 0) {
               total += num;
             }
           }
         }
       });
     });

     return total;
   };

   const proceedToNextStep = () => {
     const updatedFormData = { ...formData };
     handleSubmit(updatedFormData);
     
     const hasFilledData = (section, itemType) => {
       if (!updatedFormData[section]) return false;
       const item = updatedFormData[section][itemType];
       return item && Object.entries(item).some(([field, value]) => {
         return value && value.trim && value.trim() !== '';
       });
     };

     setRequesterInitialData({
       environment: updatedFormData.general.environment,
       projectName: updatedFormData.general.cr_project_name,
       ...updatedFormData,
       msisdnTest: hasFilledData('Prepaid', 'MSISDN'),
       prepaidstarterpackTest:
         hasFilledData('Starter Pack', 'RM5') ||
         hasFilledData('Starter Pack', 'RM10') ||
         hasFilledData('Starter Pack', 'RM30') ||
         hasFilledData('Starter Pack', 'Others'),
       softpinTest:
         hasFilledData('Softpin', 'RM5') ||
         hasFilledData('Softpin', 'RM10') ||
         hasFilledData('Softpin', 'RM20') ||
         hasFilledData('Softpin', 'RM30') ||
         hasFilledData('Softpin', 'RM50') ||
         hasFilledData('Softpin', 'RM100'),
       blanksimTest: hasFilledData('Prepaid', 'Blank Sim'),
       blanksim: hasFilledData('Prepaid', 'Blank Sim'),
       softpin:
         hasFilledData('Softpin', 'RM5') ||
         hasFilledData('Softpin', 'RM10') ||
         hasFilledData('Softpin', 'RM20') ||
         hasFilledData('Softpin', 'RM30') ||
         hasFilledData('Softpin', 'RM50') ||
         hasFilledData('Softpin', 'RM100'),
       prepaidstarterpack:
         hasFilledData('Starter Pack', 'RM5') ||
         hasFilledData('Starter Pack', 'RM10') ||
         hasFilledData('Starter Pack', 'RM30') ||
         hasFilledData('Starter Pack', 'Others'),
       reloadchargecard:
         hasFilledData('Reload', 'RM5') ||
         hasFilledData('Reload', 'RM10') ||
         hasFilledData('Reload', 'RM30') ||
         hasFilledData('Reload', 'RM50'),
     });
     setShowRequesterForm(true);
     setShowFullForm(false);
   };

   const handleTotalQuantityConfirm = () => {
     setShowTotalQuantityDialog(false);
     proceedToNextStep();
   };
 
   useEffect(() => {
     if (existingWebSocket) {
       const filler = new FormAutoFiller(existingWebSocket);
       setFormFiller(filler);
 
       const handleMessage = (event) => {
         try {
           const data = JSON.parse(event.data);
           if (data.email) {
             setUserEmail(data.email);
           }
           // Listen for bedrockSRTicket and populate the main form data
           if (data.route === 'bedrockSRTicket' && data.requesterFormData) {
             let reqData = data.requesterFormData;
             if (reqData && reqData.requestDetails && typeof reqData.requestDetails === 'object') {
               reqData = reqData.requestDetails;
             }

            // Map to the field names expected by PrepaidRequesterForm
             const mappedRequesterData = {
               requesterName: reqData.requesterName || '',
               requesterEmail: reqData.requesterEmail || '',
               requesterContact: reqData.requesterContact || '',
               environment: reqData.environment ? reqData.environment.toLowerCase() : '',
               projectName: reqData.projectName || reqData.cr_project_name || '',
               division: reqData.division || '',
               department: reqData.department || '',
               hodName: reqData.hodName || '',
               purpose: reqData.purpose || '',
               location: reqData.location || '',
               testingDate: reqData.testingDate || new Date().toISOString().split('T')[0],
                // Prepaid-specific test flags
               msisdnTest: reqData.msisdnTest ?? false,
               prepaidstarterpackTest: reqData.prepaidstarterpackTest ?? false,
               softpinTest: reqData.softpinTest ?? false,
               blanksimTest: reqData.blanksimTest ?? false,
               // Production flags
               blanksim: reqData.blanksim ?? false,
               softpin: reqData.softpin ?? false,
               prepaidstarterpack: reqData.prepaidstarterpack ?? false,
               reloadchargecard: reqData.reloadchargecard ?? false,
               formId: formId
             };
 
             // Update formData to prefill the form (similar to Postpaid)
             setFormData(prevFormData => ({
               ...prevFormData,
               general: {
                 ...prevFormData.general,
                 cr_project_name: reqData.projectName || reqData.cr_project_name || prevFormData.general.cr_project_name,
                 environment: (reqData.environment != null && reqData.environment !== '') ? reqData.environment.toLowerCase() : prevFormData.general.environment
               }
             }));
 
             setRequesterInitialData(mappedRequesterData);
             setShowRequesterForm(true);
             setShowFullForm(true);
           }
         } catch (error) {
           console.error('Error parsing WebSocket message:', error);
         }
       };
 
       existingWebSocket.addEventListener('message', handleMessage);
 
       return () => {
         existingWebSocket.removeEventListener('message', handleMessage);
       };
     }
   }, [existingWebSocket, wsConnectionId, formId]);
   
     useEffect(() => {
       if (onRef) {
         onRef({
           showForm: () => setShowFullForm(true),
         });
       }
     }, [onRef]);
   
   const scrollToFirstError = (errors) => {
     const errorElements = document.querySelectorAll('[data-error="true"]');
     if (errorElements.length > 0) {
       errorElements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
     }
   };
   
   const validateForm = () => {
     const { isValid, errors: newErrors } = validateFormData(formData);
     setErrors(newErrors);
 
     if (!isValid) {
       setTimeout(() => scrollToFirstError(newErrors), 100);
     }
 
     return isValid;
   };
   
     const handleShowFullForm = () => {
       setShowFullForm(true);
       setShowRequesterForm(false);
     };
 
     useEffect(() => {
       if (ref) {
         ref.current = formRef.current;
       }
     }, [ref]);
 
     useEffect(() => {
       const event = new CustomEvent('prepaidFormState', {
         detail: showFullForm ? 'disablePrepaidForm' : 'enablePrepaidForm',
       });
       window.dispatchEvent(event);
   
       return () => {
         const resetEvent = new CustomEvent('prepaidFormState', { detail: 'enablePrepaidForm' });
         window.dispatchEvent(resetEvent);
       };
     }, [showFullForm]);
 
     const [isBlankSimActive, setIsBlankSimActive] = useState(false);
     const [isMSISDNActive, setIsMSISDNActive] = useState(false);
     const [isStarterpackActive, setIsStarterpackActive] = useState(false);
     const [isSoftpinActive, setIsSoftpinActive] = useState(false);
     const [isReloadActive, setIsReloadActive] = useState(false);
     
     useEffect(() => {
       const isFilled = isBlankSimDataFilled(formData);
       setIsBlankSimActive(isFilled);
     
       if (isFilled) {
         console.log("Blank Sim data is filled. Disabling other test data items.");
       } else {
         console.log("Blank Sim data is not filled. Re-enabling other test data items.");
       }
     }, [formData?.Prepaid?.['Blank Sim']]);
 
     useEffect(() => {
       const isFilled = isMsisdnDataFilled(formData);
       setIsMSISDNActive(isFilled);
     
       if (isFilled) {
         console.log("MSISDN data is filled. Disabling other test data items.");
       } else {
         console.log("MSISDN data is not filled. Re-enabling other test data items.");
       }
     }, [formData?.Prepaid?.['MSISDN']]);
 
     useEffect(() => {
       const isFilled = isStarterpackDataFilled(formData);
       setIsStarterpackActive(isFilled);
     
       if (isFilled) {
         if (formData.general.environment === 'testbed') {
           console.log("Starter Pack data is filled. Disabling other test data items.");
         } else if (formData.general.environment === 'production') {
           // In production, clear Blank Sim when Starter Pack is filled
           if (formData.Prepaid?.['Blank Sim']) {
             console.log("Starter Pack is filled in production. Clearing and disabling Blank Sim.");
             setFormData(prev => ({
               ...prev,
               Prepaid: {
                 ...prev.Prepaid,
                 'Blank Sim': { store_id: "", material_code: "", quantity: "" }
               }
             }));
           }
         }
       } else {
         console.log("Starter Pack data is not filled. Re-enabling other test data items.");
       }
     }, [formData?.['Starter Pack'], formData.general.environment]);
 
     useEffect(() => {
       const isFilled = isSoftpinDataFilled(formData);
       setIsSoftpinActive(isFilled);
     
       if (isFilled) {
         if (formData.general.environment === 'testbed') {
           console.log("Softpin data is filled. Disabling other test data items.");
         } else if (formData.general.environment === 'production') {
           if (formData.Prepaid?.['Blank Sim']) {
             console.log("Softpin is filled in production. Clearing and disabling Blank Sim.");
             setFormData(prev => ({
               ...prev,
               Prepaid: {
                 ...prev.Prepaid,
                 'Blank Sim': { store_id: "", material_code: "", quantity: "" }
               }
             }));
           }
         }
       } else {
         console.log("Softpin data is not filled. Re-enabling other test data items.");
       }
     }, [formData?.['Softpin'], formData.general.environment]);
      const isFieldDisabled = (section, item) => {
       // In testbed environment
       if (formData.general.environment === 'testbed') {
         // Always disable Reload in testbed environment
         if (section === 'Reload') {
           return true;
         }

         // In testbed, if any item is active, disable all others except the active one
         if (isMSISDNActive && item !== 'MSISDN') {
           return true;
         }
         if (isStarterpackActive && section !== 'Starter Pack') {
           return true;
         }
         if (isSoftpinActive && section !== 'Softpin') {
           return true;
         }
         if (isBlankSimActive && item !== 'Blank Sim') {
           return true;
         }
       }

       if (formData.general.environment === 'production') {
         // Disable MSISDN in production
         if (item === 'MSISDN') {
           return true;
         }

         if (isBlankSimActive && item !== 'Blank Sim') {
           return true;
         }
         if (item === 'Blank Sim' && (isStarterpackActive || isSoftpinActive || isReloadActive)) {
           return true;
         }
         return false;
       }
       
       return false;
     };
 
     useEffect(() => {
       if (!isBlankSimActive) {
         console.log("Blank Sim is cleared. Test data items are now enabled.");
       }
     }, [isBlankSimActive]);
 
     useEffect(() => {
       if (!isMSISDNActive) {
         console.log("MSISDN is cleared. Test data items are now enabled.");
       } else if (formData.general.environment === 'testbed') {
         console.log("MSISDN is filled in testbed. Other items are disabled.");
         clearFormData();
       }
     }, [isMSISDNActive, formData.general.environment]);
 
      useEffect(() => {
       if (!isStarterpackActive) {
         console.log("Starter Pack is cleared. Test data items are now enabled.");
       } else if (formData.general.environment === 'testbed') {
         console.log("Starter Pack is filled in testbed. Other items are disabled.");
         clearFormData();
       }
     }, [isStarterpackActive, formData.general.environment]);
 
     useEffect(() => {
       if (!isSoftpinActive) {
         console.log("Softpin is cleared. Test data items are now enabled.");
       } else if (formData.general.environment === 'testbed') {
         console.log("Softpin is filled in testbed. Other items are disabled.");
         clearFormData();
       }
     }, [isSoftpinActive, formData.general.environment]);

     // Monitor Reload section in production environment
     useEffect(() => {
       // Check if any field in Reload section is filled
       const isReloadFilled = Object.values(formData?.['Reload'] || {}).some(item => 
         item.store_id || item.material_code || item.quantity
       );
       setIsReloadActive(isReloadFilled);

       if (isReloadFilled && formData.general.environment === 'production') {
         if (formData.Prepaid?.['Blank Sim']) {
           console.log("Reload is filled in production. Clearing and disabling Blank Sim.");
           setFormData(prev => ({
             ...prev,
             Prepaid: {
               ...prev.Prepaid,
               'Blank Sim': { store_id: "", material_code: "", quantity: "" }
             }
           }));
         }
       }
     }, [formData?.['Reload'], formData.general.environment]);
 
     const handleInputChange = (event, section, item) => {
       handleFormInputChange(event, section, item, formData, setFormData);
     };
 
     const clearFormData = () => {
       clearFormState(formData, setFormData);
     };
 
     useEffect(() => {
       if (isBlankSimActive) {
         console.log("Blank Sim data is filled. Disabling other test data items.");
         clearFormData();
       } else {
         console.log("Blank Sim data is not filled. Re-enabling other test data items.");
       }
     }, [isBlankSimActive]);
 
     // Track the previous environment value to detect changes
     const [prevEnvironment, setPrevEnvironment] = useState(null);
     
    useEffect(() => {
      // Only clear data if the environment field is actually being changed and we had a previous value
 
      if (prevEnvironment === formData.general.environment) {
        return;
      }
         
      if ('production' === formData.general.environment) {
        console.log(`Environment changed to Production. Clearing some test data.`);

        const freshFormData = {
          ...formData,
          general: formData.general,
          Prepaid: {
            'MSISDN': { store_id: "", material_code: "", quantity: "" },
            'Blank Sim': formData.Prepaid?.['Blank Sim'] ?? { store_id: "", material_code: "", quantity: "" },
          },
          'Starter Pack': formData['Starter Pack'] ?? {
            'RM5': { store_id: "", material_code: "", quantity: "" },
            'RM10': { store_id: "", material_code: "", quantity: "" },
            'RM30': { store_id: "", material_code: "", quantity: "" },
            'Others': { store_id: "", material_code: "", quantity: "" }
          },
          'Softpin': formData['Softpin'] ?? {
            'RM5': { store_id: "", material_code: "", quantity: "" },
            'RM10': { store_id: "", material_code: "", quantity: "" },
            'RM20': { store_id: "", material_code: "", quantity: "" },
            'RM30': { store_id: "", material_code: "", quantity: "" },
            'RM50': { store_id: "", material_code: "", quantity: "" },
            'RM100': { store_id: "", material_code: "", quantity: "" }
          },
          'Reload': formData['Reload'] ?? {
            'RM5': { store_id: "", material_code: "", quantity: "" },
            'RM10': { store_id: "", material_code: "", quantity: "" },
            'RM30': { store_id: "", material_code: "", quantity: "" },
            'RM50': { store_id: "", material_code: "", quantity: "" },
            'RM100': { store_id: "", material_code: "", quantity: "" }
          }
        };

        setFormData(freshFormData);
        setIsMSISDNActive(false);
      } else if ('testbed' === formData.general.environment) {
        console.log(`Environment changed to Testbed. Clearing some test data.`);
       
        const freshFormData = {
          ...formData,
          general: formData.general,
          Prepaid: {
            'MSISDN': { store_id: "", material_code: "", quantity: "" },
            'Blank Sim': { store_id: "", material_code: "", quantity: "" },
          },
          'Starter Pack': {
            'RM5': { store_id: "", material_code: "", quantity: "" },
            'RM10': { store_id: "", material_code: "", quantity: "" },
            'RM30': { store_id: "", material_code: "", quantity: "" },
            'Others': { store_id: "", material_code: "", quantity: "" }
          },
          'Softpin': {
            'RM5': { store_id: "", material_code: "", quantity: "" },
            'RM10': { store_id: "", material_code: "", quantity: "" },
            'RM20': { store_id: "", material_code: "", quantity: "" },
            'RM30': { store_id: "", material_code: "", quantity: "" },
            'RM50': { store_id: "", material_code: "", quantity: "" },
            'RM100': { store_id: "", material_code: "", quantity: "" }
          },
          'Reload': {
            'RM5': { store_id: "", material_code: "", quantity: "" },
            'RM10': { store_id: "", material_code: "", quantity: "" },
            'RM30': { store_id: "", material_code: "", quantity: "" },
            'RM50': { store_id: "", material_code: "", quantity: "" },
            'RM100': { store_id: "", material_code: "", quantity: "" }
          }
        };

        if (isMsisdnDataFilled(formData)) {
          freshFormData.Prepaid.MSISDN = formData.Prepaid?.['MSISDN'];
        } else if (isBlankSimDataFilled(formData)) {
          freshFormData.Prepaid['Blank Sim'] = formData.Prepaid?.['Blank Sim'];
        } else if (isStarterpackDataFilled(formData)) {
          freshFormData['Starter Pack'] = formData['Starter Pack'];
        } else if (isSoftpinDataFilled(formData)) {
          freshFormData['Softpin'] = formData['Softpin'];
        }
         
        setFormData(freshFormData);
        setIsReloadActive(false);
      }

      setPrevEnvironment(formData.general.environment);

    }, [formData.general.environment, prevEnvironment]);

   return (
         <Box
             component="form"
             noValidate
             ref={formRef}
             onSubmit={(event) => {
               event.preventDefault();
               const isValid = validateForm();
               if (!isValid) {
                 return;
               }
               
               // Calculate total quantity
               const totalQuantity = calculateTotalQuantity(formData);
               
               // Only show dialog if quantity exceeds 100
               if (totalQuantity > 100) {
                 setShowTotalQuantityDialog(true);
               } else {
                 // Proceed directly to next step
                 proceedToNextStep();
               }
             }}
           >
         {!showFullForm && (
           <Box sx={{ textAlign: 'center', mt: 4 }}>
             <Button variant="contained" color="primary" size="large" onClick={handleShowFullForm}>
               Click to open previous form filled up
             </Button>
           </Box>
         )}
   
         {showFullForm && (
             <Box sx={{ display: 'flex', gap: 2 }}>
               <Box sx={{ flex: 1 }}>
               <TableContainer component={Paper}>
                 <Table>
                   <TableBody>
                     {/* Header Information */}
                     {Object.entries(formData.general).map(([key, value]) => (
                       <TableRow
                         key={key}
                         data-error={!!errors.general[key]}
                         sx={{
                           backgroundColor: errors.general[key] ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                         }}
                       >
                         <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                           {key === 'cr_project_name' ? 'CR/Project Name' : key.replace(/_/g, ' ').toUpperCase()}
                         </TableCell>
                         <TableCell colSpan={3}>
                           {key === 'validity_period' ? (
                             <Box>
                               <Select
                                 fullWidth
                                 name={key}
                                 value={value}
                                 onChange={(e) => handleInputChange(e, 'general', key)}
                                 error={!!errors.general[key]}
                               >
                                 <MenuItem value="90">90 days</MenuItem>
                                 <MenuItem value="180">180 days</MenuItem>
                               </Select>
                               {errors.general[key] && (
                                 <FormHelperText error>{errors.general[key]}</FormHelperText>
                               )}
                             </Box>
                           ) : key === 'environment' ? (
                             <Box>
                               <Select
                                 fullWidth
                                 name={key}
                                 value={value}
                                 onChange={(e) => handleInputChange(e, 'general', key)}
                                 error={!!errors.general[key]}
                               >
                                 <MenuItem value="production">Production</MenuItem>
                                 <MenuItem value="testbed">Testbed</MenuItem>
                               </Select>
                               {errors.general[key] && (
                                 <FormHelperText error>{errors.general[key]}</FormHelperText>
                               )}
                             </Box>
                           ) : (
                             <Box>
                               <TextField
                                 fullWidth
                                 name={key}
                                 value={value}
                                 onChange={(e) => handleInputChange(e, 'general', key)}
                                 error={!!errors.general[key]}
                                 helperText={errors.general[key]}
                               />
                             </Box>
                           )}
                         </TableCell>
                       </TableRow>
                     ))}
                     {errors.general.test_data && (
                       <TableRow
                         data-error="true"
                         sx={{
                           backgroundColor: 'rgba(255, 0, 0, 0.05)'
                         }}
                       >
                         <TableCell colSpan={4}>
                           <Box sx={{ color: 'error.main', mt: 1, mb: 1 }}>
                             {errors.general.test_data}
                           </Box>
                         </TableCell>
                       </TableRow>
                     )}
                     {errors.general.total_quantity && (
                       <TableRow
                         data-error="true"
                         sx={{
                           backgroundColor: 'rgba(255, 0, 0, 0.05)'
                         }}
                       >
                         <TableCell colSpan={4}>
                           <Box sx={{ color: 'error.main', mt: 1, mb: 1, fontWeight: 'bold' }}>
                             {errors.general.total_quantity}
                           </Box>
                         </TableCell>
                       </TableRow>
                     )}

                     {/* Store ID, Material Code, Quantity section */}
                     <TableRow>
                         <TableCell sx={{ fontWeight: 'bold', color:'Red' }} colSpan={4}>
                           Note: fields that are highlighted in grey cannot be filled up 
                           {formData.general.environment === 'testbed' ? 
                             " (Reload is not available in testbed environment). MSISDN, Blank Sim, Starterpack and Softpin has to request separately." :
                             " (Blank Sim has to be requested separately)"}
                         </TableCell>
                     </TableRow>
 
                     {/* Test Data Sections */}
                     {Object.entries(formData).filter(([key]) => key !== 'general').map(([section, items]) => (
                       <React.Fragment key={section}>
                         <TableRow>
                           <TableCell sx={{ fontWeight: 'bold' }} colSpan={4}>{section}</TableCell>
                         </TableRow>
                         <PrepaidTestDataSection
                           section={section}
                           items={items}
                           formData={formData}
                           errors={errors}
                           isFieldDisabled={isFieldDisabled}
                           handleInputChange={handleInputChange}
                         />
                       </React.Fragment>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
             </Box>
           </Box>
       )}
       {showFullForm && (
         <Box sx={{ mt: 4, textAlign: 'center' }}>
           <Button type="submit" variant="contained" color="primary" size="large">
             NEXT
           </Button>
         </Box>
       )}
       <Box sx={{ display: showRequesterForm ? 'block' : 'none' }}>
         <RequesterForm
           initialData={requesterInitialData}
           requesterData={requesterInitialDataProp}
           ws={formFiller?.ws}
           resetTrigger={requesterResetTrigger}
           onSubmit={({ status, resetPreviousForm }) => {
             if (status === 'success' && resetPreviousForm) {
               // Reset all form fields to their initial state
               const freshInitialState = {
                 ...JSON.parse(JSON.stringify(initialFormData)),
                 general: {
                   cr_project_name: '',
                   environment: '',
                   validity_period: '',
                 },
                 Prepaid: Object.keys(initialFormData.Prepaid).reduce((acc, key) => ({
                   ...acc,
                   [key]: {
                     store_id: '',
                     material_code: '',
                     quantity: ''
                   }
                 }), {})
               };

               // Reset the form data completely
               setFormData(freshInitialState);
               
               // Notify generalApp.js component about the state reset so it can update its state too
               if (onFormDataChange) {
                 onFormDataChange(freshInitialState);
               }

               // Reset all states
               setIsBlankSimActive(false);
               setErrors({
                 general: {
                   cr_project_name: '',
                   validity_period: '',
                   environment: ''
                 }
               });

               // Reset the RequesterForm state by changing the resetTrigger
               setRequesterResetTrigger(Date.now());

             }
           }}
         />
       </Box>

       {/* Total Quantity Dialog - modal popup shown only when quantity exceeds 100 */}
       <TotalQuantityDialog
         open={showTotalQuantityDialog}
         onClose={() => setShowTotalQuantityDialog(false)}
         onConfirm={handleTotalQuantityConfirm}
         formData={formData}
         category="prepaid"
         maxQuantity={100}
       />
     </Box>
   );
 });

 export default PrepaidForm;